import { useState } from 'react';
import { Search, Loader2, BookOpen, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { api } from '../utils/api';
import { cn } from '../utils/api';

interface ResearchSession {
  id: string;
  query: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  results?: any;
  sources?: any[];
  createdAt: string;
  completedAt?: string;
}

export default function ResearchPage() {
  const [query, setQuery] = useState('');
  const [depth, setDepth] = useState<'quick' | 'standard' | 'deep'>('standard');
  const [isLoading, setIsLoading] = useState(false);
  const [sessions, setSessions] = useState<ResearchSession[]>([]);
  const [activeSession, setActiveSession] = useState<ResearchSession | null>(null);

  const startResearch = async () => {
    if (!query.trim()) return;
    
    setIsLoading(true);
    try {
      const res: any = await api.post('/research', { query, depth });
      const newSession: ResearchSession = {
        id: res.sessionId,
        query: query.trim(),
        status: 'running',
        createdAt: new Date().toISOString(),
      };
      setSessions(prev => [newSession, ...prev]);
      setActiveSession(newSession);
      setQuery('');
      
      // Poll for results
      pollResearchStatus(res.sessionId);
    } catch (error) {
      console.error('Research failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const pollResearchStatus = async (sessionId: string) => {
    const interval = setInterval(async () => {
      try {
        const res: any = await api.get(`/research/${sessionId}`);
        const session = res.session;
        
        setSessions(prev => prev.map(s => s.id === sessionId ? session : s));
        setActiveSession(session);
        
        if (session.status === 'completed' || session.status === 'failed') {
          clearInterval(interval);
        }
      } catch (error) {
        clearInterval(interval);
      }
    }, 3000);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <div className="w-80 border-r border-legion-border bg-legion-card/50 flex flex-col">
        <div className="p-4 border-b border-legion-border">
          <h2 className="font-semibold flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-400" />
            Research Sessions
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto">
          {sessions.map(session => (
            <button
              key={session.id}
              onClick={() => setActiveSession(session)}
              className={cn(
                "w-full text-left p-3 border-b border-legion-border hover:bg-white/5 transition-colors",
                activeSession?.id === session.id && "bg-white/10"
              )}
            >
              <div className="flex items-start gap-2">
                {getStatusIcon(session.status)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{session.query}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(session.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {!activeSession ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-2xl w-full text-center">
              <BookOpen className="w-16 h-16 text-indigo-400 mx-auto mb-6" />
              <h1 className="text-3xl font-bold mb-4">Deep Research</h1>
              <p className="text-gray-400 mb-8">
                Enter a topic to conduct comprehensive research with web search, 
                academic sources, and AI synthesis.
              </p>

              {/* Input */}
              <div className="space-y-4">
                <div className="relative">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="What would you like to research?"
                    className="w-full px-4 py-3 bg-legion-card border border-legion-border rounded-xl focus:outline-none focus:border-indigo-500 transition-colors"
                    onKeyDown={(e) => e.key === 'Enter' && startResearch()}
                  />
                </div>

                {/* Depth selector */}
                <div className="flex justify-center gap-2">
                  {(['quick', 'standard', 'deep'] as const).map((d) => (
                    <button
                      key={d}
                      onClick={() => setDepth(d)}
                      className={cn(
                        "px-4 py-2 rounded-lg text-sm capitalize transition-colors",
                        depth === d
                          ? "bg-indigo-600 text-white"
                          : "bg-legion-card border border-legion-border hover:border-indigo-500/50"
                      )}
                    >
                      {d}
                    </button>
                  ))}
                </div>

                <button
                  onClick={startResearch}
                  disabled={!query.trim() || isLoading}
                  className={cn(
                    "w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2",
                    (!query.trim() || isLoading) && "opacity-70 cursor-not-allowed"
                  )}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Starting research...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      Start Research
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                {getStatusIcon(activeSession.status)}
                <div>
                  <h2 className="text-xl font-semibold">{activeSession.query}</h2>
                  <p className="text-sm text-gray-500">
                    Status: {activeSession.status}
                  </p>
                </div>
              </div>

              {activeSession.status === 'running' && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-400 mx-auto mb-4" />
                    <p className="text-gray-400">Research in progress...</p>
                  </div>
                </div>
              )}

              {activeSession.status === 'completed' && activeSession.results && (
                <div className="space-y-6">
                  <div className="bg-legion-card border border-legion-border rounded-xl p-6">
                    <h3 className="font-semibold mb-4">Research Results</h3>
                    <div className="prose prose-invert max-w-none">
                      {typeof activeSession.results === 'string' 
                        ? activeSession.results 
                        : JSON.stringify(activeSession.results, null, 2)}
                    </div>
                  </div>

                  {activeSession.sources && activeSession.sources.length > 0 && (
                    <div className="bg-legion-card border border-legion-border rounded-xl p-6">
                      <h3 className="font-semibold mb-4">Sources</h3>
                      <ul className="space-y-2">
                        {activeSession.sources.map((source: any, i: number) => (
                          <li key={i} className="text-sm">
                            <a 
                              href={source.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-indigo-400 hover:underline"
                            >
                              {source.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
